import { get } from "@ember/object";
import ReimbursementsRetrievedJournalEvent from "cnam/models/reimbursements-retrieved-journal-event";
import { CnamApiDmpParsingFileError } from "cnam/utils/cnam-api-errors";
import convert from "xml-js";
import BiologicalActDmpParser from "./dmp-parsing/biological-act-dmp-parser";
import DrugsAndDevicesDmpParser from "./dmp-parsing/drugs-and-devices-dmp-parser";
import HospitalisationDmpParser from "./dmp-parsing/hospitalisation-dmp-parser";
import MedicalActDmpParser from "./dmp-parsing/medical-act-dmp-parser";
import MedicalImagingDmpParser from "./dmp-parsing/medical-imaging-dmp-parser";
import VaccinesDmpParser from "./dmp-parsing/vaccines-dmp-parser";
import { arrayIfNeeded } from "./xml-parsed-helpers";

/* The structured DMP contains several sections, each representing a type of reimbursement:
- `Treatments` section
- `Medical Devices` section
- `Encounter Histories` section (hospitalisations)
- `Procedures and Interventions` section (either of type `Soins médicaux & dentaires`,
  Radiologie` or `Biologie`)

Each section has at least one entry which corresponds to a reimbursement.

The JE we want to create are:
- `drugs-and-devices-refunded-journal-event`
- `hospitalisation-refunded-journal-event`
- `medical-act-refunded-journal-event`
- `medical-imaging-refunded-journal-event`
- `biological-act-refunded-journal-event`

Each type of JE has its own parser which parses its corresponding section, and extracts fields
that are required to create the JE. The parser are:

- `drugs-and-devices-dmp-parser` : it parses two sections, `Treatments` and `Medical Devices`,
  and, for each entry in these sections, it extracts fields that are required to create a
  `drugs-and-devices-refunded-journal-event`- `hospitalisation-dmp-parser` : it parses the
  section `Encounter Histories` and, for each entry extracts fields that are required to create
  a `hospitalisation-refunded-journal-event`

- `medical-act-dmp-parser` : it parses the section `Procedures and Interventions`, of type
  `Soins médicaux & dentaires` and, for each entry extracts fields that are required to create
  a `medical-act-refunded-journal-event`

- `medical-imaging-dmp-parser` : it parses the section `Procedures and Interventions`, of type
  `Radiologie` and, for each entry extracts fields that are required to create a
  `medical-imaging-refunded-journal-event`

- `biological-act-dmp-parser` : it parses the section `Procedures and Interventions`, of type
  `Biologie` and, for each entry extracts fields that are required to create a
  `biological-act-refunded-journal-event`

When the loop over each type of parser is done, we can create the `reimbursements-retrieved-journal-event`. */

export default async function createReimbursementFromXML(xml, store, patient, cnamProcessStartedJournalEvent) {
  let jsonString;
  try {
    jsonString = convert.xml2json(xml, {
      compact: true,
      spaces: 2
    });
  } catch (error) {
    throw new CnamApiDmpParsingFileError("xml file corrupted, impossible to parse", error);
  }
  const json = JSON.parse(jsonString);
  const components = arrayIfNeeded(
  // eslint-disable-next-line ember/no-get
  get(json, "ClinicalDocument.component.structuredBody.component"));
  const parsers = [new DrugsAndDevicesDmpParser(), new HospitalisationDmpParser(), new MedicalActDmpParser(), new BiologicalActDmpParser(), new MedicalImagingDmpParser(), new VaccinesDmpParser()];
  const reimbursements = {};
  components.forEach(component => {
    const section = component.section;
    parsers.forEach(parser => {
      if (parser.canParse(section)) {
        const parsedContent = parser.parse(section);
        if (!parsedContent) return;
        if (!reimbursements[parser.journalEventClassName]) {
          reimbursements[parser.journalEventClassName] = [];
        }
        if (!Array.isArray(parsedContent) || parsedContent.length == 0) {
          return;
        }
        reimbursements[parser.journalEventClassName] = [...reimbursements[parser.journalEventClassName], ...parsedContent];
      }
    });
  });
  const reimbursementsRetrievedJE = await store.createRecord("reimbursements-retrieved-journal-event", {
    patient,
    success: true,
    structuredData: reimbursements,
    occurredAt: new Date(),
    version: ReimbursementsRetrievedJournalEvent.CURRENT_PARSING_VERSION,
    cnamProcessStartedJournalEvent
  }).save();
  await store.query("journal-event", {
    filter: {
      type: parsers.map(parser => parser.journalEventClassName),
      patient_id: patient.id
    }
  });
  return reimbursementsRetrievedJE;
}